/* ==========================================================================
   Farooze Turquoise — theme.js
   Zero-dependency, progressive-enhancement JavaScript built on native
   Web Components, <dialog>, fetch and the Section Rendering API.
   ========================================================================== */
(() => {
  'use strict';

  const FZ = (window.FZ = window.FZ || {});
  FZ.routes = FZ.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', search: '/search', predictiveSearch: '/search/suggest', recommendations: '/recommendations/products' };
  FZ.strings = FZ.strings || {};

  /* ---------- Utilities ---------- */
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const url = (path) => {
    const root = FZ.routes.root || '/';
    if (path.startsWith(root) || root === '/') return path;
    return root.replace(/\/$/, '') + path;
  };

  const formatMoney = (cents, format = FZ.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;
    const re = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(precision);
      const [int, dec] = fixed.split('.');
      return int.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (dec ? decimal + dec : '');
    };
    const match = format.match(re);
    if (!match) return fmt(cents);
    const map = {
      amount: () => fmt(cents, 2),
      amount_no_decimals: () => fmt(cents, 0),
      amount_with_comma_separator: () => fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: () => fmt(cents, 2, "'", '.'),
      amount_no_decimals_with_space_separator: () => fmt(cents, 0, ' ', ''),
      amount_with_space_separator: () => fmt(cents, 2, ' ', ','),
      amount_with_period_and_space_separator: () => fmt(cents, 2, ' ', '.')
    };
    return format.replace(re, (map[match[1]] || map.amount)());
  };
  FZ.formatMoney = formatMoney;

  const toast = (message) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };
  FZ.toast = toast;

  /* ---------- Dialogs (drawers & modals) ---------- */
  const openDialog = (id, opener) => {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  };
  FZ.openDialog = openDialog;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-dialog]');
    if (opener && document.getElementById(opener.dataset.openDialog)) {
      e.preventDefault();
      openDialog(opener.dataset.openDialog, opener);
      return;
    }
    const closer = e.target.closest('[data-close-dialog]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    // Click on the backdrop closes the dialog
    if (e.target instanceof HTMLDialogElement && e.target.open) {
      const r = e.target.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) e.target.close();
    }
  });
  document.addEventListener('close', (e) => {
    if (e.target instanceof HTMLDialogElement && e.target._opener?.isConnected) {
      e.target._opener.focus({ preventScroll: true });
    }
  }, true);

  /* ---------- Header: sticky behaviour + mega menu ---------- */
  const initHeader = () => {
    const wrapper = $('.section-header');
    if (!wrapper) return;
    const header = $('.site-header', wrapper);
    const hideOnScroll = header?.dataset.hideOnScroll === 'true';
    let lastY = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      wrapper.classList.toggle('is-scrolled', y > 8);
      if (hideOnScroll) {
        const openMega = $('.nav__item[open]', wrapper);
        if (y > lastY + 4 && y > 240 && !openMega) wrapper.classList.add('is-hidden');
        else if (y < lastY - 4 || y < 240) wrapper.classList.remove('is-hidden');
      }
      document.documentElement.style.setProperty('--header-offset', `${Math.max(0, wrapper.getBoundingClientRect().bottom)}px`);
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();

    // Mega menus: one open at a time, hover intent on pointer devices
    const items = $$('.nav__item', wrapper);
    const closeAll = (except) => items.forEach((d) => { if (d !== except) d.open = false; });
    items.forEach((details) => {
      details.addEventListener('toggle', () => { if (details.open) closeAll(details); });
      if (window.matchMedia('(hover: hover)').matches) {
        let t;
        details.addEventListener('mouseenter', () => { clearTimeout(t); t = setTimeout(() => { details.open = true; }, 90); });
        details.addEventListener('mouseleave', () => { clearTimeout(t); t = setTimeout(() => { details.open = false; }, 180); });
      }
    });
    document.addEventListener('click', (e) => { if (!e.target.closest('.nav__item')) closeAll(); });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = items.find((d) => d.open);
      if (open) { open.open = false; $('summary', open)?.focus(); }
    });
  };

  /* ---------- Announcement rotator ---------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.announcement__slide', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = Number(this.dataset.interval || 5000);
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.start();
    }
    disconnectedCallback() { this.stop(); }
    start() { if (reducedMotion()) return; this.stop(); this.timer = setInterval(() => this.go(this.index + 1), this.interval); }
    stop() { clearInterval(this.timer); }
    go(i) {
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
      this.slides[this.index].removeAttribute('aria-hidden');
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      if (!this.input || !this.results || this.dataset.enabled === 'false') return;
      this.input.setAttribute('autocomplete', 'off');
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.input.addEventListener('focus', () => { if (this.results.innerHTML.trim()) this.results.hidden = false; });
      this.addEventListener('keydown', (e) => this.onKey(e));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
    }
    close() { this.results.hidden = true; this.input.setAttribute('aria-expanded', 'false'); }
    onKey(e) {
      if (e.key === 'Escape') { this.close(); this.input.focus(); return; }
      if (!['ArrowDown', 'ArrowUp'].includes(e.key) || this.results.hidden) return;
      const links = $$('a', this.results);
      if (!links.length) return;
      e.preventDefault();
      const i = links.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % links.length : (i <= 0 ? links.length - 1 : i - 1);
      links[next].focus();
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = ''; this.close(); return; }
      this.controller?.abort();
      this.controller = new AbortController();
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      if (!this.results.innerHTML.trim()) this.results.innerHTML = '<div class="predictive__loading">…</div>';
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[type]': 'product,collection,query,page,article'
      });
      try {
        const res = await fetch(`${FZ.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parseHTML(await res.text());
        const content = $('#shopify-section-predictive-search', doc);
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Cart ---------- */
  const Cart = {
    sectionIds() {
      const ids = new Set(['cart-icon-bubble']);
      if ($('#CartDrawer')) ids.add('cart-drawer');
      $$('[data-rerender]').forEach((el) => ids.add(el.dataset.rerender));
      return Array.from(ids).slice(0, 5);
    },
    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        $$(`[data-rerender="${id}"]`).forEach((target) => {
          const source = $(`[data-rerender="${id}"]`, doc);
          if (source) target.innerHTML = source.innerHTML;
        });
      });
      $$('.cart-count').forEach((el) => {
        el.classList.remove('is-bumped');
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },
    async add(formData) {
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${FZ.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: formData
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || FZ.strings.error);
      this.render(json.sections);
      return json;
    },
    async change(line, quantity) {
      const res = await fetch(`${FZ.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || FZ.strings.error);
      this.render(json.sections);
      return json;
    },
    async note(note) {
      await fetch(`${FZ.routes.cartUpdate || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
    },
    afterAdd() {
      const drawer = $('#CartDrawer');
      if (FZ.cartType === 'drawer' && drawer) {
        $('#QuickView')?.open && $('#QuickView').close();
        openDialog('CartDrawer');
      } else {
        window.location.href = FZ.routes.cart;
      }
    }
  };
  FZ.Cart = Cart;

  // Line item quantity + remove (cart drawer and cart page)
  const changeLine = async (el, quantity) => {
    const line = Number(el.closest('[data-line]')?.dataset.line);
    if (!line) return;
    const row = el.closest('.cart-line, .cart-row');
    row?.classList.add('is-updating');
    try {
      await Cart.change(line, quantity);
    } catch (err) {
      toast(err.message);
      row?.classList.remove('is-updating');
    }
  };
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-line-remove]');
    if (remove) { e.preventDefault(); changeLine(remove, 0); }
  });
  document.addEventListener('change', debounce((e) => {
    const input = e.target.closest('[data-line-qty]');
    if (input) changeLine(input, Math.max(0, parseInt(input.value, 10) || 0));
  }, 300));
  document.addEventListener('change', debounce((e) => {
    if (e.target.matches('[data-cart-note]')) Cart.note(e.target.value);
  }, 500));

  /* ---------- Quantity input ---------- */
  class QtyInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-step]');
        if (!btn || !this.input) return;
        e.preventDefault();
        const min = Number(this.input.min || 0);
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const step = Number(this.input.step || 1);
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || 0) + step * Number(btn.dataset.step)));
        if (String(next) === this.input.value) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('qty-input', QtyInput);

  /* ---------- Product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const btn = $('[type="submit"]', this.form);
      if (!btn || btn.getAttribute('aria-disabled') === 'true' || btn.disabled) return;
      const error = $('[data-form-error]', this);
      if (error) error.hidden = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      try {
        await Cart.add(new FormData(this.form));
        Cart.afterAdd();
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
        toast(err.message);
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Quick add / quick view from product cards ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    const variantId = btn.dataset.variantId;
    if (variantId) {
      btn.classList.add('is-loading');
      try {
        const fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', '1');
        await Cart.add(fd);
        Cart.afterAdd();
      } catch (err) {
        toast(err.message);
      } finally {
        btn.classList.remove('is-loading');
      }
      return;
    }
    const productUrl = btn.dataset.productUrl;
    const modal = $('#QuickView');
    const content = $('[data-quick-view-content]', modal || document);
    if (!productUrl || !modal || !content) { window.location.href = productUrl; return; }
    content.innerHTML = '<div class="loader"><span class="spinner"></span></div>';
    openDialog('QuickView', btn);
    try {
      const sep = productUrl.includes('?') ? '&' : '?';
      const res = await fetch(`${productUrl}${sep}section_id=quick-view`);
      const doc = parseHTML(await res.text());
      const section = $('.shopify-section', doc) || doc.body;
      content.innerHTML = section.innerHTML;
    } catch (err) {
      window.location.href = productUrl;
    }
  });

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      const data = $('script[type="application/json"][data-variants]', this);
      if (!data) return;
      this.variants = JSON.parse(data.textContent);
      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }
    selected() {
      return $$('[data-option-index]', this).map((group) => {
        const checked = $('input:checked', group);
        const select = $('select', group);
        return checked ? checked.value : select ? select.value : null;
      });
    }
    onChange() {
      const options = this.selected();
      $$('[data-option-index]', this).forEach((group, i) => {
        const label = $('[data-selected-value]', group);
        if (label) label.textContent = options[i] || '';
      });
      let variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.updateAvailability();
      this.dispatchVariant(variant);
    }
    updateAvailability() {
      const selected = this.selected();
      $$('[data-option-index]', this).forEach((group, i) => {
        $$('input[type="radio"], option', group).forEach((input) => {
          const value = input.value;
          const available = this.variants.some((v) => v.available && v.options[i] === value && v.options.every((o, j) => j === i || j > i || o === selected[j]));
          const exists = this.variants.some((v) => v.options[i] === value && v.options.every((o, j) => j === i || j > i || o === selected[j]));
          input.classList.toggle('is-unavailable', !available);
          if (input.tagName === 'OPTION') {
            const base = input.dataset.label || input.value;
            input.textContent = available ? base : `${base} — ${exists ? FZ.strings.soldOut : FZ.strings.unavailable}`;
          }
        });
      });
    }
    dispatchVariant(variant) {
      const sid = this.sectionId;
      $$(`[data-variant-input="${sid}"]`).forEach((input) => { input.value = variant ? variant.id : ''; input.disabled = !variant; });

      // Price
      $$(`[data-price="${sid}"]`).forEach((el) => {
        if (!variant) return;
        const onSale = variant.compare_at_price > variant.price;
        el.classList.toggle('price--sale', onSale);
        el.innerHTML = `<span class="price__current">${formatMoney(variant.price)}</span>` +
          (onSale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : '') +
          (onSale && el.dataset.showSaving !== 'false' ? `<span class="badge badge--sale">-${Math.round((1 - variant.price / variant.compare_at_price) * 100)}%</span>` : '');
      });

      // Buttons
      $$(`[data-atc="${sid}"]`).forEach((btn) => {
        const label = $('[data-atc-label]', btn) || btn;
        if (!variant) { btn.disabled = true; label.textContent = FZ.strings.unavailable; return; }
        btn.disabled = !variant.available;
        label.textContent = variant.available ? FZ.strings.addToCart : FZ.strings.soldOut;
      });

      // SKU + stock
      $$(`[data-sku="${sid}"]`).forEach((el) => { el.textContent = variant?.sku || ''; el.closest('[data-sku-wrap]')?.toggleAttribute('hidden', !variant?.sku); });
      $$(`[data-stock="${sid}"]`).forEach((el) => {
        const threshold = Number(el.dataset.threshold || 10);
        el.classList.remove('is-low', 'is-out');
        if (!variant) { el.hidden = true; return; }
        el.hidden = false;
        const text = $('[data-stock-text]', el) || el;
        if (!variant.available) { el.classList.add('is-out'); text.textContent = FZ.strings.soldOut; }
        else if (variant.tracked && variant.qty > 0 && variant.qty <= threshold && variant.policy !== 'continue') {
          el.classList.add('is-low');
          text.textContent = (FZ.strings.lowStock || 'Only __count__ left').replace('__count__', variant.qty);
        } else { text.textContent = FZ.strings.inStock; }
      });

      if (variant && this.dataset.updateUrl === 'true') {
        const u = new URL(window.location.href);
        u.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', u.toString());
      }
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { sectionId: sid, variant } }));
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------- Product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.slides = $$('[data-media-id]', this.track || this);
      this.thumbs = $$('[data-thumb]', this);
      this.counter = $('[data-gallery-counter]', this);
      if (!this.track) return;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      $('[data-gallery-prev]', this)?.addEventListener('click', () => this.go(this.index - 1));
      $('[data-gallery-next]', this)?.addEventListener('click', () => this.go(this.index + 1));
      this.index = 0;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting && en.intersectionRatio > 0.6) this.setActive(this.slides.indexOf(en.target));
        });
      }, { root: this.track, threshold: [0.6] });
      this.slides.forEach((s) => io.observe(s));
      this.onVariant = (e) => {
        if (e.detail.sectionId !== this.dataset.section || !e.detail.variant?.media) return;
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(e.detail.variant.media));
        if (i > -1) this.go(i);
      };
      document.addEventListener('variant:change', this.onVariant);
      // Lightbox
      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-zoom]');
        if (!zoom) return;
        const box = document.getElementById(this.dataset.lightbox);
        if (!box) return;
        openDialog(box.id, zoom);
        const target = $$('[data-lightbox-item]', box)[Number(zoom.dataset.zoom)];
        requestAnimationFrame(() => target?.scrollIntoView({ block: 'center' }));
      });
    }
    disconnectedCallback() { document.removeEventListener('variant:change', this.onVariant); }
    go(i) {
      const n = this.slides.length;
      if (!n) return;
      i = (i + n) % n;
      const slide = this.slides[i];
      const left = slide.offsetLeft - this.slides[0].offsetLeft;
      this.track.scrollTo({ left, behavior: reducedMotion() ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    setActive(i) {
      if (i < 0) return;
      this.index = i;
      this.thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
      const active = this.thumbs[i];
      if (active) {
        const rail = active.parentElement;
        const railRect = rail.getBoundingClientRect();
        const r = active.getBoundingClientRect();
        if (r.left < railRect.left || r.right > railRect.right) rail.scrollBy({ left: r.left - railRect.left - 12, behavior: 'smooth' });
        if (r.top < railRect.top || r.bottom > railRect.bottom) rail.scrollBy({ top: r.top - railRect.top - 12, behavior: 'smooth' });
      }
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- Sticky add-to-cart bar ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target) return;
      const footer = $('.footer');
      let ticking = false;
      this.check = () => {
        const t = target.getBoundingClientRect();
        this.classList.toggle('is-visible', t.bottom < 0);
        if (footer) this.classList.toggle('is-hidden-footer', footer.getBoundingClientRect().top < window.innerHeight);
        ticking = false;
      };
      this.onScroll = () => { if (!ticking) { requestAnimationFrame(this.check); ticking = true; } };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.check();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Carousel ---------- */
  class XCarousel extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-carousel-track]', this);
      if (!this.track) return;
      this.prev = $('[data-carousel-prev]', this);
      this.next = $('[data-carousel-next]', this);
      this.bar = $('.carousel__progress span', this);
      const step = () => Math.max(this.track.clientWidth * 0.85, 200);
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      new ResizeObserver(() => this.update()).observe(this.track);
    }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      if (this.prev) this.prev.disabled = scrollLeft <= 4;
      if (this.next) this.next.disabled = scrollLeft >= max - 4;
      if (this.bar) {
        const ratio = clientWidth / scrollWidth;
        this.bar.style.setProperty('--p', `${Math.max(ratio * 100, 8)}%`);
        this.bar.style.setProperty('--x', `${max > 0 ? (scrollLeft / max) * ((1 / ratio - 1) * 100) : 0}%`);
        this.bar.parentElement.hidden = max <= 4;
      }
    }
  }
  customElements.define('x-carousel', XCarousel);

  /* ---------- Tabs ---------- */
  class XTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('pointerenter', () => this.fill(document.getElementById(tab.getAttribute('aria-controls'))), { once: true });
      });
      this.addEventListener('keydown', (e) => {
        if (!['ArrowRight', 'ArrowLeft'].includes(e.key) || !e.target.matches('[role="tab"]')) return;
        const i = this.tabs.indexOf(e.target) + (e.key === 'ArrowRight' ? 1 : -1);
        const t = this.tabs[(i + this.tabs.length) % this.tabs.length];
        t.focus();
        this.select(t);
      });
    }
    select(tab) {
      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on);
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !on;
        if (on) this.fill(panel);
      });
    }
    /* Fetch the products of a tab that was left out of the initial HTML.
       A tab must never end up empty: on failure we fall back to a link to the
       collection and leave the tab retryable. */
    async fill(panel) {
      const host = panel && $('[data-tab-lazy]', panel);
      if (!host || host.dataset.filled) return;
      host.dataset.filled = 'busy';
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const res = await fetch(host.dataset.tabLazy, { headers: { Accept: 'text/html' } });
          if (!res.ok) throw new Error(res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const grid = $('[data-tab-grid]', doc);
          if (!grid || !grid.children.length) throw new Error('empty');
          host.innerHTML = grid.innerHTML;
          host.dataset.filled = 'done';
          panel.querySelector('x-carousel')?.update?.();
          return;
        } catch (err) {
          if (attempt === 0) {
            await new Promise((r) => setTimeout(r, 700));
            continue;
          }
          $$('.card--skeleton', host).forEach((n) => n.remove());
          const fallback = $('.tab-fallback', host);
          if (fallback) fallback.hidden = false;
          host.dataset.filled = '';
        }
      }
    }
  }
  customElements.define('x-tabs', XTabs);

  /* ---------- Countdown ---------- */
  class CountDown extends HTMLElement {
    connectedCallback() {
      this.end = new Date(this.dataset.end).getTime();
      if (Number.isNaN(this.end)) { this.hidden = true; return; }
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      let diff = Math.max(0, this.end - Date.now());
      if (diff === 0) {
        clearInterval(this.timer);
        if (this.dataset.hideExpired === 'true') this.closest('[data-countdown-wrap]')?.setAttribute('hidden', '');
      }
      const units = { days: 86400000, hours: 3600000, minutes: 60000, seconds: 1000 };
      Object.entries(units).forEach(([k, ms]) => {
        const v = Math.floor(diff / ms);
        diff -= v * ms;
        const el = $(`[data-${k}]`, this);
        if (el) el.textContent = String(v).padStart(2, '0');
      });
    }
  }
  customElements.define('count-down', CountDown);

  /* ---------- Delivery estimate ---------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const add = (days) => {
        const d = new Date();
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (d.getDay() !== 0 && d.getDay() !== 6) added++;
        }
        return d;
      };
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { month: 'short', day: 'numeric' });
      const out = $('[data-delivery-dates]', this);
      if (out) out.textContent = `${fmt.format(add(Number(this.dataset.min)))} – ${fmt.format(add(Number(this.dataset.max)))}`;
      this.hidden = false;
    }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Cart upsell (lazy product recommendations) ---------- */
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.productId || this.loaded) return;
      const load = () => this.load();
      const drawer = this.closest('dialog');
      if (drawer && !drawer.open) drawer.addEventListener('dialog:open', load, { once: true });
      else load();
    }
    async load() {
      this.loaded = true;
      try {
        const res = await fetch(`${FZ.routes.recommendations}?product_id=${this.dataset.productId}&limit=8&section_id=cart-upsell&intent=related`);
        const doc = parseHTML(await res.text());
        const inner = $('[data-upsell-inner]', doc);
        if (inner && inner.children.length) { this.innerHTML = inner.outerHTML; this.hidden = false; }
      } catch (e) { /* recommendations are optional */ }
    }
  }
  customElements.define('cart-upsell', CartUpsell);

  /* ---------- Product recommendations section ---------- */
  class ProductRecs extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loaded) return;
      // This element renders empty and is filled from the recommendations
      // endpoint. It was previously gated on IntersectionObserver, which never
      // fired for it — an empty custom element has no box to intersect — so the
      // section stayed blank on every product page. Defer past first paint
      // instead: same benefit, and it always runs.
      const start = () => this.load();
      if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 2000 });
      else setTimeout(start, 500);
    }
    async load() {
      this.loaded = true;
      // The section already rendered same-category products server-side, so a
      // failed or empty response is not a problem — we simply keep what is
      // there. Only upgrade when Shopify actually returned something.
      try {
        const res = await fetch(this.dataset.url);
        const doc = parseHTML(await res.text());
        const fresh = $('product-recs', doc);
        if (fresh && fresh.querySelector('.card')) { this.innerHTML = fresh.innerHTML; return; }
      } catch (e) { /* keep the server-rendered fallback */ }
      if (!this.querySelector('.card')) this.closest('.shopify-section')?.setAttribute('hidden', '');
    }
  }
  customElements.define('product-recs', ProductRecs);

  /* ---------- Collection & search filters (Section Rendering API) ---------- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = $('form[data-facets-form]', this);
      if (!this.form) return;
      this.onChange = debounce(() => this.apply(this.queryFromForm()), 350);
      this.addEventListener('input', (e) => { if (e.target.form === this.form || e.target.closest('form[data-facets-form]')) this.onChange(); });
      this.addEventListener('change', (e) => { if (e.target.tagName === 'SELECT' && e.target.form === this.form) this.onChange(); });
      this.addEventListener('submit', (e) => { e.preventDefault(); this.apply(this.queryFromForm()); });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-facet-link]');
        if (link) { e.preventDefault(); this.apply(new URL(link.href, window.location.origin).searchParams.toString()); }
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); }
      });
      window.addEventListener('popstate', () => this.apply(window.location.search.slice(1), false));
    }
    queryFromForm() {
      const fd = new FormData(this.form);
      const params = new URLSearchParams();
      for (const [k, v] of fd.entries()) if (v !== '') params.append(k, v);
      return params.toString();
    }
    async apply(query, push = true) {
      const results = $('[data-facets-results]', this);
      results?.classList.add('is-loading');
      const openIds = $$('details[open][id]', this).map((d) => d.id);
      try {
        const q = query ? `${query}&` : '';
        const res = await fetch(`${window.location.pathname}?${q}section_id=${this.sectionId}`);
        const doc = parseHTML(await res.text());
        ['[data-facets-results]', '[data-facets-form-body]', '[data-facets-active]', '[data-facets-count]', '[data-facets-badge]'].forEach((sel) => {
          const fresh = $(sel, doc);
          $$(sel, this).forEach((el) => { if (fresh) el.innerHTML = fresh.innerHTML; });
        });
        openIds.forEach((id) => { const d = document.getElementById(id); if (d) d.open = true; });
        if (push) window.history.pushState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
      } catch (e) {
        window.location.search = query;
      } finally {
        $('[data-facets-results]', this)?.classList.remove('is-loading');
      }
    }
    async loadMore(btn) {
      const next = btn.dataset.nextUrl;
      if (!next) return;
      btn.classList.add('is-loading');
      try {
        const sep = next.includes('?') ? '&' : '?';
        const res = await fetch(`${next}${sep}section_id=${this.sectionId}`);
        const doc = parseHTML(await res.text());
        const grid = $('[data-product-grid]', this);
        $$('[data-product-grid] > *', doc).forEach((item) => grid.appendChild(item));
        const freshMore = $('[data-load-more-wrap]', doc);
        const wrap = $('[data-load-more-wrap]', this);
        if (wrap) wrap.innerHTML = freshMore ? freshMore.innerHTML : '';
        observeReveal(grid);
      } catch (e) {
        window.location.href = next;
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ---------- Share ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-share]');
    if (!btn) return;
    e.preventDefault();
    const data = { title: btn.dataset.title || document.title, url: btn.dataset.url || window.location.href };
    try {
      if (navigator.share) await navigator.share(data);
      else { await navigator.clipboard.writeText(data.url); toast(FZ.strings.copied); }
    } catch (err) { /* user cancelled */ }
  });

  /* ---------- Auto-submit selects (localization, sort without JS form) ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-auto-submit]')) e.target.form?.submit();
  });

  /* ---------- Reveal on scroll ---------- */
  let revealIO;
  const observeReveal = (root = document) => {
    if (!document.body.classList.contains('has-reveal') || reducedMotion()) {
      $$('[data-reveal]', root).forEach((el) => el.classList.add('is-in'));
      return;
    }
    revealIO = revealIO || new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); revealIO.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    $$('[data-reveal]:not(.is-in)', root).forEach((el) => revealIO.observe(el));
  };
  FZ.observeReveal = observeReveal;

  /* ---------- Boot ---------- */
  const boot = () => {
    initHeader();
    document.body.classList.add('reveal-ready');
    observeReveal();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    observeReveal(e.target);
    if (e.target.querySelector('.site-header')) initHeader();
  });
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = e.target.querySelector('dialog.drawer');
    if (drawer && !drawer.open && e.target.id.includes('cart-drawer')) drawer.showModal();
  });
})();
